import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ShadingType,
  ExternalHyperlink,
} from 'docx';
import { marked } from 'marked';

const HEADING_LEVELS = [
  HeadingLevel.HEADING_1,
  HeadingLevel.HEADING_2,
  HeadingLevel.HEADING_3,
  HeadingLevel.HEADING_4,
  HeadingLevel.HEADING_5,
  HeadingLevel.HEADING_6,
];

type RunStyle = { bold?: boolean; italics?: boolean; strike?: boolean };

// Converts marked's inline token tree (text/strong/em/del/codespan/link/br)
// into docx runs, carrying bold/italic/strike down through nested emphasis.
const inlineToRuns = (tokens: any[] = [], style: RunStyle = {}): (TextRun | ExternalHyperlink)[] => {
  const runs: (TextRun | ExternalHyperlink)[] = [];
  for (const t of tokens) {
    switch (t.type) {
      case 'strong':
        runs.push(...inlineToRuns(t.tokens, { ...style, bold: true }));
        break;
      case 'em':
        runs.push(...inlineToRuns(t.tokens, { ...style, italics: true }));
        break;
      case 'del':
        runs.push(...inlineToRuns(t.tokens, { ...style, strike: true }));
        break;
      case 'codespan':
        runs.push(new TextRun({ text: t.text, font: 'Consolas', ...style }));
        break;
      case 'link':
        runs.push(
          new ExternalHyperlink({
            link: t.href,
            children: [new TextRun({ text: t.text, color: '2563EB', underline: {}, ...style })],
          })
        );
        break;
      case 'br':
        runs.push(new TextRun({ text: '', break: 1 }));
        break;
      default:
        runs.push(new TextRun({ text: t.text ?? t.raw ?? '', ...style }));
    }
  }
  return runs;
};

// Converts one marked block token into docx block elements. `indent` tracks
// list depth so nested bullets, and a list item's extra paragraphs, are
// indented under their item.
const blockToElements = (token: any, indent = 0): (Paragraph | Table)[] => {
  switch (token.type) {
    case 'heading':
      return [
        new Paragraph({
          heading: HEADING_LEVELS[Math.min(token.depth, 6) - 1],
          children: inlineToRuns(token.tokens),
        }),
      ];

    case 'text':
    case 'paragraph':
      return [
        new Paragraph({
          children: inlineToRuns(token.tokens ?? [{ type: 'text', text: token.text }]),
          indent: indent ? { left: indent * 360 } : undefined,
          spacing: { after: 160 },
        }),
      ];

    case 'blockquote':
      return (token.tokens ?? []).flatMap((inner: any) =>
        inner.type === 'paragraph'
          ? [
              new Paragraph({
                indent: { left: 360 },
                border: { left: { style: BorderStyle.SINGLE, size: 12, color: 'CCCCCC', space: 8 } },
                children: inlineToRuns(inner.tokens, { italics: true }),
                spacing: { after: 160 },
              }),
            ]
          : blockToElements(inner, indent)
      );

    case 'list': {
      const elements: (Paragraph | Table)[] = [];
      token.items.forEach((item: any, i: number) => {
        const leadIndex = item.tokens.findIndex((t: any) => t.type === 'text' || t.type === 'paragraph');
        const runs = leadIndex >= 0 ? inlineToRuns(item.tokens[leadIndex].tokens) : [];
        elements.push(
          new Paragraph({
            indent: { left: 360 + indent * 360 },
            bullet: token.ordered ? undefined : { level: indent },
            children: token.ordered ? [new TextRun(`${(token.start || 1) + i}. `), ...runs] : runs,
            spacing: { after: 60 },
          })
        );
        item.tokens.forEach((child: any, j: number) => {
          if (j !== leadIndex) elements.push(...blockToElements(child, indent + 1));
        });
      });
      return elements;
    }

    case 'table': {
      const cellCount = token.header.length || 1;
      const makeCell = (cell: any, isHeader: boolean) =>
        new TableCell({
          width: { size: 100 / cellCount, type: WidthType.PERCENTAGE },
          shading: isHeader ? { type: ShadingType.CLEAR, fill: 'EEF2FF' } : undefined,
          children: [new Paragraph({ children: inlineToRuns(cell.tokens, isHeader ? { bold: true } : {}) })],
        });
      return [
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ children: token.header.map((c: any) => makeCell(c, true)), tableHeader: true }),
            ...token.rows.map((row: any) => new TableRow({ children: row.map((c: any) => makeCell(c, false)) })),
          ],
        }),
      ];
    }

    case 'code': {
      const lines: string[] = (token.text ?? '').split('\n');
      return [
        new Paragraph({
          shading: { type: ShadingType.CLEAR, fill: 'F5F5F5' },
          children: lines.flatMap((line, i) => [
            new TextRun({ text: line, font: 'Consolas', size: 20 }),
            ...(i < lines.length - 1 ? [new TextRun({ text: '', break: 1 })] : []),
          ]),
          spacing: { after: 160 },
        }),
      ];
    }

    case 'hr':
      return [new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'CCCCCC' } }, spacing: { after: 200 } })];

    case 'space':
      return [];

    default:
      return token.text ? [new Paragraph({ children: [new TextRun(token.text)] })] : [];
  }
};

export const downloadReportAsWord = async (markdown: string, fileName: string): Promise<void> => {
  const tokens = marked.lexer(markdown);
  const children = tokens.flatMap((t) => blockToElements(t));

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const buildDeepResearchFileName = (timestamp: string): string => {
  const d = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `Deep-Research-Report-${stamp}.docx`;
};
